import clsx from "clsx";
import { useEffect, useId, useRef, useState } from "react";

import type { Campaign } from "../../api/generated";
import type { CampaignPickerProps } from "../types";

import { BottomSheet } from "../../BottomSheet";
import {
  BaseAlertTriangleIcon,
  CheckPaddedIcon,
  CrossFieldIcon,
  PlusIcon,
  SearchIcon,
} from "../../Icons";
import { ResponsiveSpinner } from "../../ResponsiveSpinner";
import { Typo } from "../../Typo";
import { CampaignPickerTrigger } from "../CampaignPickerTrigger";
import { useCampaignPickerController } from "../hooks/useCampaignPickerController";
import { useCampaignTranslations } from "../hooks/useCampaignTranslations";

export type CampaignPickerMobileProps = CampaignPickerProps;

export const CampaignPickerMobile = ({
  id,
  label,
  subLabel,
  value,
  campaigns,
  onSelect,
  onCreate,
  onCreateSuccess,
  createError,
  disabled = false,
  translations: translationsProp,
  className,
  search,
  onSearchChange,
  isSearchLoading = false,
  isLoading = false,
  hasNextPage = false,
  onLoadMore,
  isLoadingMore = false,
  maxLength,
}: CampaignPickerMobileProps) => {
  const defaultTranslations = useCampaignTranslations();
  const translations = translationsProp ?? defaultTranslations;

  const generatedId = useId();
  const fieldId = id ?? generatedId;

  const resolvedLabel = label ?? translations.label;
  const resolvedSubLabel = subLabel ?? translations.subLabel;

  const triggerRef = useRef<HTMLInputElement | null>(null);
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const wasOpenRef = useRef(false);

  const [isSheetOpen, setIsSheetOpen] = useState(false);

  const closeSheetAndRestoreFocus = () => {
    setIsSheetOpen(false);
    triggerRef.current?.focus();
  };

  const {
    query,
    trimmedQuery,
    filteredCampaigns,
    showCreateRow,
    isCreating,
    isMasked,
    handleQueryChange,
    resetQuery,
    handleSelect,
    handleCreate,
    handleResultsScroll,
  } = useCampaignPickerController({
    campaigns,
    onSelect,
    onCreate,
    onCreateSuccess,
    search,
    onSearchChange,
    isSearchLoading,
    isLoading,
    hasNextPage,
    onLoadMore,
    onCommitted: closeSheetAndRestoreFocus,
    maxLength,
  });

  const maskAriaLabel =
    !isLoading && isSearchLoading ? translations.searchLoadingLabel : undefined;

  const options: Campaign[] = filteredCampaigns;

  const effectiveValue =
    value ?? campaigns.find((campaign) => campaign.isDefault) ?? null;

  useEffect(() => {
    if (isSheetOpen && !wasOpenRef.current) {
      resetQuery();
      searchInputRef.current?.focus();
    }
    wasOpenRef.current = isSheetOpen;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSheetOpen]);

  return (
    <div className={clsx("flex w-full flex-col gap-y-2", className)}>
      <CampaignPickerTrigger
        id={fieldId}
        label={resolvedLabel}
        subLabel={resolvedSubLabel}
        value={effectiveValue}
        placeholder={translations.placeholder}
        open={isSheetOpen}
        disabled={disabled}
        error={createError}
        errorId={isSheetOpen ? undefined : `${fieldId}-error`}
        onActivate={() => {
          if (disabled) return;
          setIsSheetOpen(true);
        }}
        inputRef={triggerRef}
      />

      <BottomSheet
        isOpen={isSheetOpen}
        onClose={closeSheetAndRestoreFocus}
        onBack={closeSheetAndRestoreFocus}
        backLabel={resolvedLabel}
        isDraggable={trimmedQuery === ""}
      >
        <div className="flex flex-col gap-4">
          <div className="flex h-10 items-center gap-2 rounded-lg bg-surface-tonal-default p-2 focus-within:ring-2 focus-within:ring-stroke-focused">
            <SearchIcon className="h-4 w-4 shrink-0 text-icon-tertiary" />
            <input
              ref={searchInputRef}
              type="text"
              role="searchbox"
              aria-label="Search campaigns"
              value={query}
              onChange={(event) => handleQueryChange(event.target.value)}
              placeholder={translations.searchPlaceholder}
              maxLength={maxLength}
              className="typo-body-sm placeholder:typo-body-sm min-w-0 flex-1 bg-transparent text-text-primary placeholder-text-tertiary focus:outline-none"
            />
            {query && (
              <button
                type="button"
                onClick={() => handleQueryChange("")}
                className="flex shrink-0 items-center text-icon-secondary hover:text-icon-primary focus:outline-none"
                aria-label="Clear search"
              >
                <CrossFieldIcon className="h-4 w-4" />
              </button>
            )}
          </div>

          {isMasked ? (
            <div className="flex items-center justify-center p-4">
              <ResponsiveSpinner
                height={20}
                width={20}
                role="status"
                aria-label={maskAriaLabel}
                color="text-icon-brand-default"
              />
            </div>
          ) : (
            <>
              {/* `listbox` can only contain `option` children — create row/empty state/footer are siblings, not inside it. */}
              <div
                data-testid="campaign-picker-mobile-results"
                className="max-h-[384px] overflow-y-auto"
                onScroll={handleResultsScroll}
              >
                {options.length > 0 && (
                  <div
                    role="listbox"
                    aria-label={resolvedLabel}
                    aria-busy={isLoadingMore}
                  >
                    {options.map((option) => {
                      const isSelected = effectiveValue?.id === option.id;
                      return (
                        <div
                          key={option.id}
                          role="option"
                          data-testid={`campaign_picker_mobile_campaign_option_${option.id}`}
                          aria-selected={isSelected}
                          className={clsx(
                            "flex cursor-pointer items-center justify-between gap-2 rounded-lg p-2 hover:bg-surface-tonal-default",
                            isSelected && "bg-state-selected",
                          )}
                          onClick={() => handleSelect(option)}
                        >
                          <Typo
                            element="p"
                            type="bodySM"
                            textColor={
                              isSelected ? "text-text-selected" : undefined
                            }
                            className="min-w-0 flex-1 break-words"
                          >
                            {option.name}
                          </Typo>
                          {isSelected && (
                            <CheckPaddedIcon
                              className="h-6 w-6 shrink-0 text-icon-selected"
                              aria-hidden="true"
                            />
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
                {trimmedQuery === "" && campaigns.length === 0 && (
                  <div className="p-2">
                    <Typo
                      element="p"
                      type="bodySM"
                      textColor="text-text-secondary"
                    >
                      {translations.noCampaignsLabel}
                    </Typo>
                  </div>
                )}
                {showCreateRow && !isCreating && (
                  <div
                    role="button"
                    tabIndex={0}
                    aria-label={translations.createOptionLabel(trimmedQuery)}
                    className="flex cursor-pointer items-center gap-2 rounded-lg p-2 hover:bg-surface-tonal-default"
                    onClick={() => void handleCreate(trimmedQuery)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        void handleCreate(trimmedQuery);
                      }
                    }}
                  >
                    <PlusIcon className="h-4 w-4 shrink-0 text-icon-brand-default" />
                    <Typo
                      element="p"
                      type="labelSM"
                      textColor="text-text-brand-default"
                      className="min-w-0 flex-1 break-words"
                    >
                      {translations.createOptionLabel(trimmedQuery)}
                    </Typo>
                  </div>
                )}
                {(isLoadingMore || isCreating) && (
                  <div
                    role="status"
                    aria-label={
                      isCreating ? translations.creatingLabel : undefined
                    }
                    className="flex items-center justify-center gap-2 p-2"
                  >
                    <ResponsiveSpinner
                      height={16}
                      width={16}
                      color="text-icon-brand-default"
                    />
                  </div>
                )}
              </div>
              {createError && (
                <div role="alert" className="flex gap-1">
                  <div className="flex items-center py-px text-icon-error">
                    <BaseAlertTriangleIcon className="h-4 w-4" />
                  </div>
                  <Typo element="p" type="bodyXS" textColor="text-text-error">
                    {createError}
                  </Typo>
                </div>
              )}
            </>
          )}
        </div>
      </BottomSheet>
    </div>
  );
};

export default CampaignPickerMobile;
